import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { array, object, string, date, number } from "yup";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  useTheme,
  InputAdornment,
} from "@mui/material";
import {
  Receipt,
  Calendar,
  DollarSign,
  User,
  CreditCard,
  Hash
} from 'lucide-react';
import KeycloakService from "../config/KeycloakService";
import Constants from "../../utils/constants/Constants";
import MultiPaymentForm from "./MultiPaymentForm";
import {
  formatDateTimeForInput,
  formatDateTimeForBackend,
  isDateTimeInThePast,
} from "../../utils/functions/Helpers";
import { ToastContainer } from "react-toastify";
import { createReceipt } from "./ReceiptService";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import { tokens } from "../../theme";
import {
  calculateReceiptTotals,
  getReceiptFormInitialValues,
  normalizeReceiptPayments,
} from "./receiptFormUtils";

const paymentSchema = object().shape({
  method: string().required("Payment method is required"),
  amount: number("Amount must be a number")
    .required("Amount is required")
    .positive("Amount should be positive"),
  cardLastFour: string().when("method", {
    is: Constants.PAYMENT_METHODS.card,
    then: (schema) =>
      schema
        .required("Last 4 digits of the card are required")
        .length(4, "Should be exactly 4 digits"),
  }),
  otherDetails: string().when("method", {
    is: Constants.PAYMENT_METHODS.other,
    then: (schema) => schema.required("Payment method details are required"),
  }),
});

const receiptSchema = object().shape({
  id: number().notRequired(),
  receiptNumber: string().notRequired(),
  receiptDate: date().notRequired(),
  amountPaid: number("Amount paid must be a number")
    .required("Amount paid is required")
    .positive("Amount should be positive"),
  reservationId: number().required("Reservation ID is required"),
  userId: string().required("User ID is required"),
  invoiceId: number().required("Invoice ID is required"),
  payments: array()
    .of(paymentSchema)
    .test(
      "is-total-amount-paid-greather-than-balance-due",
      "Total amount paid cannot be greater than balance due.",
      function (value) {
        return this.parent.balanceDue >= 0;
      }
    ),
  paidBy: string().required("Paid by is required"),
  paymentDate: date("Payment date must be a date")
    .transform((value, originalValue) => {
      return originalValue ? new Date(originalValue) : null;
    })
    .test("is-date-in-the-past", "Date must be in the past", (value) =>
      isDateTimeInThePast(value)
    )
    .required("Payment date is required"),
  prevBalance: number("Previous balance must be a number")
    .required("Previous balance is required")
    .min(0, "Amount should be positive or equal to 0"),
  balanceDue: number("Balance Due must be a number").required(
    "Balance due is required"
  ),
  cashierName: string().required("Cashier name is required"),
});

const ReceiptForm = (props) => {
  const {
    invoice,
    selectedUserId,
    selectedInvoiceId,
    selectedReservationId,
    receiptToEdit,
    updateReceipt,
    toggleModal,
    setState,
    isUpdating = false,
  } = props;

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const setIsLoading = useLoadingSpinner();
  const setApiError = useState(false)[1];

  const {
    clearErrors,
    control,
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(receiptSchema),
    defaultValues: getReceiptFormInitialValues({
      invoice,
      receiptToEdit,
      cashierName: KeycloakService.getUserFullName(),
      formatDateTimeForInput,
      selectedUserId,
      selectedInvoiceId,
      selectedReservationId,
    }),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "payments",
  });

  const watchedPayments = useWatch({ control, name: "payments" });
  const receiptDate = useWatch({ control, name: "receiptDate" });
  const prevBalance = useWatch({ control, name: "prevBalance" });
  const cashierName = useWatch({ control, name: "cashierName" });

  const normalizedPayments = useMemo(
    () => (Array.isArray(watchedPayments) ? watchedPayments : []),
    [watchedPayments]
  );

  const computedTotals = useMemo(
    () => calculateReceiptTotals(normalizedPayments, prevBalance),
    [normalizedPayments, prevBalance]
  );

  useEffect(() => {
    reset(
      getReceiptFormInitialValues({
        invoice,
        receiptToEdit,
        cashierName: KeycloakService.getUserFullName(),
        formatDateTimeForInput,
        selectedUserId,
        selectedInvoiceId,
        selectedReservationId,
      })
    );
  }, [
    invoice,
    receiptToEdit,
    reset,
    selectedInvoiceId,
    selectedReservationId,
    selectedUserId,
  ]);

  useEffect(() => {
    setValue("amountPaid", computedTotals.amountPaid, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue("balanceDue", computedTotals.balanceDue, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [computedTotals, setValue]);

  const handleCancel = () => {
    clearErrors();
    reset();
    toggleModal !== undefined && toggleModal(false);
  };

  const onSubmit = async (data) => {
    data.receiptDate = formatDateTimeForBackend(data.receiptDate);
    data.paymentDate = formatDateTimeForBackend(data.paymentDate);
    data.payments = normalizeReceiptPayments(data.payments);
    data.amountPaid = computedTotals.amountPaid;
    data.balanceDue = computedTotals.balanceDue;

    setIsLoading(true);
    setApiError(false);
    try {
      if (isUpdating) {
        await updateReceipt(data, setState, setApiError, setIsLoading);
      } else {
        await createReceipt(data, setState, setApiError, setIsLoading);
      }
      reset();
      toggleModal !== undefined && toggleModal(false);
    } catch (err) {
      console.error("Submission failed", err);
    }
  };

  const handlePaymentMethodRemove = (index) => {
    remove(index);
    clearErrors(`payments[${index}].method`);
    clearErrors(`payments[${index}].amount`);
  };

  const handlePaymentMethodAmountChange = (onChange) => (e) => {
    onChange(e.target.value);
  };

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : "#FFFFFF",
        p: { xs: 2, md: 5 },
        borderRadius: "24px",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" alignItems="center" gap={1.5} mb={4}>
          <Receipt size={32} color={colors.greenAccent[500]} />
          <Box>
            <Typography variant="h3" fontWeight="700" sx={{ color: colors.grey[100], fontFamily: "Josefin Sans" }}>
              {isUpdating ? "Refine Receipt" : "Acknowledge Payment"}
            </Typography>
            <Typography variant="body1" sx={{ color: colors.grey[300] }}>
              Finalizing the financial record for this engagement.
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={4}>
          {/* Timeline Section */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="600" color={colors.greenAccent[500]} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Calendar size={18} /> Timeline Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Receipt Generation Date"
                  type="datetime-local"
                  {...register("receiptDate")}
                  value={receiptDate ?? ""}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Transaction Capture Date"
                  type="datetime-local"
                  {...register("paymentDate")}
                  error={!!errors.paymentDate}
                  helperText={errors.paymentDate?.message}
                  max={new Date().toISOString().slice(0, 16)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Financials Section */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="600" color={colors.greenAccent[500]} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <DollarSign size={18} /> Financial Ledger
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Previous Ledger Balance"
                  type="number"
                  {...register("prevBalance")}
                  value={prevBalance ?? 0}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <DollarSign size={18} color={colors.grey[400]} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Applied Payment"
                  type="number"
                  {...register("amountPaid")}
                  value={computedTotals.amountPaid}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <DollarSign size={18} color={colors.grey[400]} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Remaining Balance"
                  type="number"
                  {...register("balanceDue")}
                  value={computedTotals.balanceDue}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <DollarSign size={18} color={colors.grey[400]} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Multi-Payment Section */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="600" color={colors.greenAccent[500]} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CreditCard size={18} /> Transaction Channels
            </Typography>
            <Box sx={{ mt: 2 }}>
              <MultiPaymentForm
                addPayment={() => append({ method: "", amount: 0 })}
                amountChangeHandler={handlePaymentMethodAmountChange}
                control={control}
                Controller={Controller}
                errors={errors}
                fields={fields}
                register={register}
                methods={normalizedPayments}
                removePayment={handlePaymentMethodRemove}
              />
              {errors.payments && !Array.isArray(errors.payments) && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  {errors.payments.message}
                </Typography>
              )}
              {(errors.userId || errors.invoiceId || errors.reservationId) && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  The selected account is missing invoice context. Go back to step one and reselect the host/invoice.
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Verification Section */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="600" color={colors.greenAccent[500]} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <User size={18} /> Official Verification
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Subscriber Name / Paid By"
                  {...register("paidBy")}
                  error={!!errors.paidBy}
                  helperText={errors.paidBy?.message}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <User size={18} color={colors.grey[400]} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Authorizing Officer"
                  {...register("cashierName")}
                  value={cashierName ?? ""}
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Hash size={18} color={colors.grey[400]} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCancel}
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                borderColor: colors.redAccent[500],
                color: colors.redAccent[500],
                "&:hover": { borderColor: colors.redAccent[600], bgcolor: `${colors.redAccent[500]}0D` }
              }}
            >
              Discard Entry
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: "700",
                bgcolor: colors.greenAccent[500],
                color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                "&:hover": { bgcolor: colors.greenAccent[400], boxShadow: `0 8px 16px ${colors.greenAccent[500]}33` }
              }}
            >
              {isUpdating ? 'Update Official Record' : 'Record Transaction'}
            </Button>
          </Grid>
        </Grid>
      </form>
      <ToastContainer />
    </Box>
  );
};

ReceiptForm.propTypes = {
  invoice: PropTypes.object,
  selectedUserId: PropTypes.string,
  selectedInvoiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedReservationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  receiptToEdit: PropTypes.object,
  updateReceipt: PropTypes.func,
  toggleModal: PropTypes.func,
  setState: PropTypes.func,
  isUpdating: PropTypes.bool,
};

export default ReceiptForm;
